import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import signalIcon from '@/assets/icons/header/contacts/hugeicons_signal.svg';
import whatsappIcon from '@/assets/icons/header/contacts/ic_baseline-whatsapp.svg';
import telegramIcon from '@/assets/icons/header/contacts/ri_telegram-2-line.svg';
import viberIcon from '@/assets/icons/header/contacts/basil_viber-outline.svg';
import emailIcon from '@/assets/icons/header/contacts/ic_outline-email.svg';
import linkedinIcon from '@/assets/icons/header/contacts/linked_in-outline.svg';
import calendarIcon from '@/assets/icons/header/contacts/mynaui_calendar.svg';
import burgerIcon from '@/assets/icons/header/material-symbols_menu-rounded.svg';
import closeIcon from '@/assets/icons/header/material-symbols_close.svg';

import signalIconPurple from '@/assets/icons/header/contacts-purple/hugeicons_signal.svg';
import whatsappIconPurple from '@/assets/icons/header/contacts-purple/ic_baseline-whatsapp.svg';
import telegramIconPurple from '@/assets/icons/header/contacts-purple/ri_telegram-2-line.svg';
import viberIconPurple from '@/assets/icons/header/contacts-purple/basil_viber-outline.svg';
import emailIconPurple from '@/assets/icons/header/contacts-purple/ic_outline-email.svg';
import linkedinIconPurple from '@/assets/icons/header/contacts-purple/linked_in-outline.svg';
import calendarIconPurple from '@/assets/icons/header/contacts-purple/mynaui_calendar.svg';

import { LINKS } from '@/constants/LINKS';

import { handleAnchorLinkClick } from '@/utils/handleAnchorLinkClick';
import { getHeaderHeight } from '@/utils/getHeaderHeight';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useScreenType } from '@/hooks/useScreenType';

import { Container } from '../Container';
import { LanguageSelect } from '../LanguageSelect';
import { Overlay } from '../Overlay';
import { Contact } from '../Contact';

import styles from './Header.module.scss';

export function Header() {
  const { t } = useTranslation('header');
  const scrollPosition = useScrollPosition();
  const [isBurgerOpen, setIsBurgerOpen] = useState(false);
  const { screenType } = useScreenType();
  const isMobileDevice = screenType === 'tablet' || screenType === 'mobile';

  const handleMenuItemClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    setIsBurgerOpen(false);
    handleAnchorLinkClick(e, targetId, getHeaderHeight(screenType));
  };

  function getPhoneIcons(purple = false) {
    return [
      {
        href: LINKS.signal,
        icon: purple ? signalIconPurple : signalIcon,
        alt: 'Signal',
      },
      {
        href: LINKS.whatsapp,
        icon: purple ? whatsappIconPurple : whatsappIcon,
        alt: 'WhatsApp',
      },
      {
        href: LINKS.telegram,
        icon: purple ? telegramIconPurple : telegramIcon,
        alt: 'Telegram',
      },
      {
        href: LINKS.viber,
        icon: purple ? viberIconPurple : viberIcon,
        alt: 'Viber',
      },
    ];
  }

  return (
    <>
      <header
        className={clsx(
          styles.header,
          { [styles.header_colored]: scrollPosition > 0 },
          {
            [styles.header_expanded]: isBurgerOpen && isMobileDevice,
          },
        )}
      >
        <Container className={styles.header__container}>
          <div className={styles.header__contacts}>
            <div className={styles.header__contactsWrapper}>
              <Contact
                icons={getPhoneIcons(isMobileDevice)}
                text="+380978865177"
                textRef="tel:+380978865177"
              />
              <Contact
                icons={[
                  {
                    href: LINKS.email,
                    icon: isMobileDevice ? emailIconPurple : emailIcon,
                    alt: 'Email',
                  },
                ]}
                text="annatransfer2016@gmail.com"
              />
              <Contact
                icons={[
                  {
                    href: LINKS.linkedin,
                    icon: isMobileDevice ? linkedinIconPurple : linkedinIcon,
                    alt: 'LinkedIn',
                  },
                ]}
                text="Anna Transfer"
              />
            </div>
            <Contact
              icons={[
                {
                  icon: isMobileDevice ? calendarIconPurple : calendarIcon,
                  alt: 'Calendar',
                },
              ]}
              text={t('workingHours')}
            />
          </div>
          <LanguageSelect
            className={clsx(styles.header__languageSelect, {
              [styles.header__languageSelect_disabled]: !isBurgerOpen,
            })}
          />
          {isMobileDevice && (
            <Contact
              className={clsx(styles.header__mobileTopContact, {
                [styles.header__mobileTopContact_disabled]: isBurgerOpen,
              })}
              icons={getPhoneIcons(false)}
              text="+380978865177"
              textRef="tel:+380978865177"
            />
          )}
          <nav className={styles.header__menu}>
            <a
              href="#about-us"
              className={styles.header__menuItem}
              onClick={(e) => handleMenuItemClick(e, 'about-us')}
            >
              {t('menu.aboutUs')}
            </a>
            <a
              href="#car-park"
              className={styles.header__menuItem}
              onClick={(e) => handleMenuItemClick(e, 'car-park')}
            >
              {t('menu.carPark')}
            </a>
            <a
              href="#footer"
              className={styles.header__menuItem}
              onClick={(e) => handleMenuItemClick(e, 'footer')}
            >
              {t('menu.contacts')}
            </a>
            <a
              href="#faq"
              className={styles.header__menuItem}
              onClick={(e) => handleMenuItemClick(e, 'faq')}
            >
              {t('menu.faq')}
            </a>
            <a
              href="#reviews"
              className={styles.header__menuItem}
              onClick={(e) => handleMenuItemClick(e, 'reviews')}
            >
              {t('menu.reviews')}
            </a>
          </nav>
          <button className={styles.header__burger} onClick={() => setIsBurgerOpen(!isBurgerOpen)}>
            <img src={isBurgerOpen ? closeIcon : burgerIcon} alt="Burger" />
          </button>
        </Container>
      </header>
      <Overlay visible={isBurgerOpen && isMobileDevice} onClick={() => setIsBurgerOpen(false)} />
    </>
  );
}
